/**
 * @fileoverview Library file preview — kind classification.
 *
 * Split out of `LibraryFilePreview.tsx` (react-refresh/only-export-components)
 * — classifies a file into a live-preview kind, pure and unit-testable
 * without mounting the renderer.
 */

import type { FileSystemFileItem } from "@/ui/composed/file-system/file-system";

const WORD_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export type PreviewKind = "docx" | "xlsx" | "csv" | "text";

/**
 * Classify a file into a live-preview kind, or null when the Finder's own
 * thumbnail pipeline already handles it (image/pdf) or no preview exists.
 */
export function libraryPreviewKind(
  file: Pick<FileSystemFileItem, "name" | "path" | "contentType">,
): PreviewKind | null {
  const type = file.contentType ?? "";
  const ext = (file.name ?? file.path).split(".").pop()?.toLowerCase() ?? "";

  // Images and PDFs render through the Finder's thumbnail pipeline already.
  if (type.startsWith("image/") || type === "application/pdf") return null;

  if (
    type === WORD_MIME ||
    type === "application/msword" ||
    ["docx", "doc", "dotx", "dot"].includes(ext)
  ) {
    return "docx";
  }
  if (
    type === EXCEL_MIME ||
    type === "application/vnd.ms-excel" ||
    ["xlsx", "xls"].includes(ext)
  ) {
    return "xlsx";
  }
  if (
    type === "text/csv" ||
    type === "text/tab-separated-values" ||
    ["csv", "tsv"].includes(ext)
  ) {
    return "csv";
  }
  if (
    type.startsWith("text/") ||
    type === "application/json" ||
    type === "application/xml" ||
    ["txt", "json", "md", "log", "xml", "yaml", "yml"].includes(ext)
  ) {
    return "text";
  }
  return null;
}
